import {uploadService} from '../upload';
import {calcFileMd5} from '../../utils/md5';
import {createChunks, getObsKeyFromSignedUrl, shouldUseMultipart} from '../../utils/file';
import {uploadToObs} from '../../utils/obs';
import {runWithConcurrency} from '../../utils/upload/runWithConcurrency';
import type {UploadedObsFile, UploadOption} from '../../types/upload';

export async function uploadFileToObsOnly(file: File, options?: UploadOption): Promise<UploadedObsFile> {
  const md5 = await calcFileMd5(file);

  const uploaded = shouldUseMultipart(file)
    ? await uploadByMultipart(file, options)
    : await uploadBySingleToObs(file, options);

  const obsKey = getObsKeyFromSignedUrl(uploaded.obs_key);

  return {
    obs_key: obsKey,
    file_name: file.name,
    file_md5: md5,
  };
}

export async function uploadBySingleToObs(file: File, options?: UploadOption): Promise<UploadedObsFile> {
  const uploadUrl = await uploadService.getSingleUploadUrl({
    filename: file.name,
  });

  const obsKey = getObsKeyFromSignedUrl(uploadUrl);

  await uploadToObs(uploadUrl, file, (percent) => {
    options?.onProgress?.(percent);
  });

  return {
    obs_key: obsKey,
    file_name: file.name,
  };
}

export async function uploadByMultipart(file: File, options?: UploadOption): Promise<UploadedObsFile> {
  const initRes = await uploadService.initMultipart({
    filename: file.name,
  });

  const uploadId = initRes.upload_id;
  const objectKey = initRes.object_key;

  const chunks = createChunks(file);

  let uploadedCount = 0;

  const tasks = chunks.map((chunk, index) => async () => {
    const partNumber = index + 1;

    const signRes = await uploadService.getPartUploadUrl({
      upload_id: uploadId,
      object_key: objectKey,
      part_number: partNumber,
    });

    const res = await fetch(signRes.signedUrl, {
      method: 'PUT',
      body: chunk,
    });

    if (!res.ok) {
      throw new Error(`分片${partNumber}上传失败`);
    }

    const etag = res.headers.get('etag') || '';

    uploadedCount += 1;

    const percent = Math.floor((uploadedCount / chunks.length) * 100);
    options?.onProgress?.(percent);

    return {
      part_number: partNumber,
      etag: JSON.parse(etag)
    };
  });

  const parts = await runWithConcurrency(tasks, 3);

  await uploadService.completeMultipart({
    upload_id: uploadId,
    object_key: objectKey,
    parts: parts.sort((a, b) => a.part_number - b.part_number),
  });

  return {
    obs_key: objectKey,
    file_name: file.name,
  };
}

export async function uploadFilesToObs(
  file: File,
  options?: UploadOption
): Promise<UploadedObsFile> {
  return shouldUseMultipart(file)
    ? await uploadByMultipart(file, options)
    : await uploadBySingleToObs(file, options);
}